import * as EventTypes from "../../../application/EventTypes";
import { BvhNode, NodeStateUpdateOperations } from './BvhNode';
import { setGPU_MEMORY_LIMIT, GPU_MEMORY_LIMIT, USE_WEBGPU } from "../../globals";
import { analytics } from "../../../analytics/index";
import { getDefaultStageConfiguration, getRcsStageConfiguration, StageNames, ResourceType } from "./tasks/defaultStageConfiguration";
import { isMobileDevice } from "../../../compat";
import { Prefs3D } from '../../../application/PreferenceNames';
import { PriorityDeque } from "./PriorityDeque";

// Binary search for insertion function that uses a sort compatible compare function
const sortedInsertPosition = (array, value, compare) => {
  let low = 0;
  let high = array.length;

  while (low < high) {
    let mid = low + high >>> 1;
    if (compare(array[mid], value) < 0) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  return low;
};

export class Stage {
  /** @type {OutOfCoreTileManagerStageConfiguration} */
  config;
  /** @type {PriorityDeque} */
  nodes = new PriorityDeque({ compare: (a, b) => a.compare(b) });
  /** @type {BvhNode[]} */
  inAsyncPromotion = [];
  /** @type {Stage} */
  nextStage;
  /** @type {Stage} */
  previousStage;
  /** @type {number} Index in the stages array */
  index;

  constructor(config) {
    this.config = config;
  }
}

/** @import { RenderModel } from "../RenderModel" */
/** @import { Viewer3DImpl } from "../../../application/Viewer3DImpl" */
/** @import { ModelIteratorBVH } from "../ModelIteratorBVH" */
/** @import { WebGLRenderer } from "../../render/WebGLRenderer" */

/**
 * @typedef {Object} OutOfCoreTileManagerStageConfiguration
 *
 * @property {StageNames} stage                         - The name of the stage
 * @property {number}     screenSpaceErrorScalingFactor - Scaling factor for the screenspace errors.
 *                                                        Note: these MUST be monotonically decreasing, as otherwise
 *                                                              nodes might get stuck in upper stages.
 * @property {Function[]} taskInitializationFunctions   - Functions to create new tasks when the stage has been reached
 * @property {ResourceType} resource                    - The resource that is used by a node when it gets promoted into this stage
 * @property {Function} prerequisites                   - A function that checks whether a node can be promoted to this stage.
 * @property {function(BvhNode, Stage, number): number} computeScreenSpaceError
 *                                                      - A function that computes the screen space error for a node
 *                                                        for a given stage.
 */

/**
 * @typedef {Object} ActivePromotion
 *
 * @property {BvhNode} node
 * @property {Promise[]} taskCompletionPromises - Promises to be waited for until the node has completed the promotion
*/

/**
 * @typedef {Function} OneShotTaskFunction
 * A function that is executed once during task processing
 *
 * @returns {boolean} - Whether the task has been completed
 */

/**
 * @typedef {Object} OutOfCoreTileManagerStats
 * @property {number} tilesInQueue
 * @property {number} tilesOnGpu
 * @property {number} promotedLastSecond
 * @property {number} demotedLastSecond
 * @property {number} promotedThisFrame
 * @property {number} demotedThisFrame
 * @property {Map<ResourceType, { consumed: number, limit: number }>} resources
 */

/**
 * Class responsible for managing the resources for the BVH tiles
 *
 * @alias Autodesk.Viewing.Private.OutOfCoreTileManager
 * @private
 */
export class OutOfCoreTileManager {
  // Global data structures that are shared across all viewer instances and models

  /** @type {Map<ResourceType, { consumed: number, limit: number }} */
  static #resources = new Map();

  static #stats = {
    promotedThisFrame: 0,
    demotedThisFrame: 0,
    promotedLastSecond: 0,
    demotedLastSecond: 0,
    promotedThisSecond: 0,
    demotedThisSecond: 0
  };

  static #statsUpdateTimer;

  /** @type {Map<Model|string, OutOfCoreTileManager} */
  static modelManagerRegistry = new Map();

  /** @type {{priority: (Number|BvhNode), task: OneShotTaskFunction}[]} */
  static oneShotTasks = [];

  /** @type {Function[]} */
  static recurringTasks = [];

  // Instance data structures that are specific to a model
  #frameCounts = [];

  /** @type {RenderModel} */
  model;

  /** @type {Array<BvhNode>} can be sparse! */
  bvhNodes = [];

  /** @type {Map<number, Set<number>>} */
  iteratorLockedTiles = new Map();

  /** @type {Viewer3DImpl[]} */
  #viewers = [];

  /** @type {ModelIteratorBVH[]} */
  iteratorIdRegistry = [];

  /** @type {Stage[]} - these are shared between OOCTMs with the same config */
  stages;

  /** @type {Map<string, Stage>} - Maps from stage name to stage object */
  stagesByName = new Map();

  /** @type {Number} - start time stamp when model was created */
  static #t0_ModelCreation = undefined;

  /** @type {Number} - end time stamp for GPU upload reaching the memory limit if so */
  static #t1_GPUUpload = undefined;

  /** @type {Map<OutCoreTileManagerStageConfiguration, Stage[]>} */
  static _stagesByConfig = new Map();

  /** @type {Stage[]} - flat _stagesByConfig for easier iterating */
  static _allStages = [];

  /** @type {Map<ResourceType, Stage[]>} */
  static _stagesByResourceForDemotion = new Map();

  /** @type {ActivePromotion} The currently active promotion */
  static currentPromotion = undefined;

  /** @type {number} timeout to call executeTasks again after addOneShotTask was called */
  static resumeExecutingTasksTimeout = undefined;

  /** @type {number} The most recently used deadline. Used by resumeExecutingTasksTimeout */
  static lastDeadline = undefined;

  /** @type {{promise: Promise<void>, resolve: function(): void, reject: function(): void}} Promise and resolvers, internally used by __debugWaitForPendingTasks() */
  static _resolveWhenTasksDone = undefined;

  /** @type {boolean} - Tracks if there were download tasks this tick (used by OtgResourceCache coordination) */
  static #hadDownloadTasksThisTick = false;

  /**
   * Ring buffer of arrays, one for each frame in the staleness window.
   * When a node is rendered on frame N, it's added to array[N % 10].
   * On frame N, we check nodes in array[N % 10] (which were added 10 frames ago)
   * for staleness and clear that array.*
   * @type {BvhNode[][]}
   */
  static #recentlyRenderedNodes = Array.from({ length: 10 }, () => []);

  /** @type {number} - Ring buffer index for staleness tracking. Incremented every frame */
  static #recentlyRenderedNodeIndex = 0;

  /**
   * Notifies that there were download tasks for OOCTM processing.
   */
  static notifyHadDownloadTasks() {
    OutOfCoreTileManager.#hadDownloadTasksThisTick = true;
  }

  /**
   * Checks if there were download tasks last tick and resets the flag.
   * Used by OtgResourceCache to determine if OOCTM downloads should take priority over the legacy queue.
   * Just checking whether any download tasks were executed last tick is not enough,
   * because the OOCTM might have other things to do for a few ticks.
   * This particular implementation requires that notifyHadDownloadTasks is called in every tick,
   * which currently happens in the prerequisite check that is done every tick.
   *
   * @returns {boolean} - Whether there were download tasks in the last tick
   */
  static hadDownloadTasksLastTick() {
    const hadDownloadTasks = OutOfCoreTileManager.#hadDownloadTasksThisTick;
    OutOfCoreTileManager.#hadDownloadTasksThisTick = false;
    return hadDownloadTasks;
  }

  /**
   * Creates a new OutOfCoreTileManager
   * @param {RenderModel} model - The model for which this tile manager is responsible
   * @param {OutOfCoreTileManagerStageConfiguration[]} [stageConfiguration] - The configuration for the stages
   */
  constructor(model, stageConfiguration = model.loader?.getStageConfiguration?.() ?? getDefaultStageConfiguration()) {
    this.model = model;

    this.stages = OutOfCoreTileManager.#registerStageConfig(stageConfiguration);

    this.stages.forEach((stage) => this.stagesByName.set(stage.config.stage, stage));

    OutOfCoreTileManager.setResourceLimit(ResourceType.CPU_MEMORY, /*Infinity*/4_000_000_000);
    if (!USE_WEBGPU) {
      OutOfCoreTileManager.setResourceLimit(ResourceType.GPU_MEMORY, GPU_MEMORY_LIMIT);
    }
  }

  /**
   * Returns the BVH node for the given node index.
   *
   * @param {number} nodeIdx
   * @returns {BvhNode}
   */
  getBvhNode(nodeIdx) {
    return this.bvhNodes[nodeIdx];
  }

  /**
   * Initialized the BVH node for the given node index (it it doesn't already exist).
   * If a new node ist created, it will be assigned to the first stage.
   *
   * @param {number} nodeIdx - Index of the node
   * @param {boolean} transparent - is this node transparent?
   */
  initializeBvhNode(nodeIdx, transparent) {
    if (this.bvhNodes[nodeIdx]) {
      return this.bvhNodes[nodeIdx];
    }

    const node = new BvhNode(nodeIdx, this.model, this, this.stages[0], transparent);
    this.bvhNodes[nodeIdx] = node;
    node.currentStage.nodes.push(node);
    return node;
  }

  /**
   * Releases an iterator, if it is no longer used (when the consolidation iterator that
   * references it is being destroyed or the model is removed from the viewer)
   * @param {number} iteratorId
   */
  releaseIterator(iteratorId) {
    this.resetScreenSpaceErrors(iteratorId);
    this.resetLockedTiles(iteratorId);
    this.iteratorIdRegistry[iteratorId] = undefined;

    if (this.iteratorIdRegistry.every((id) => id === undefined)) {
      // If the last iterator that is using this OOCTM is gone, we discard all nodes.
      // This is important if the BVH is recomputed at runtime like for Tandem.
      // Some background information...
      //  - The 'keepNodesInLowestStage=false' is important for the case of a BVH recomputation:
      //    The new BVHIterator will add new nodes that must not be mixed with the old ones.
      //  - In other scenarios without dynamic BVH recomputation, it doesn't matter (but doesn't hurt either) whether we use true or false,
      //    because the whole OutOfCoreTileManager is thrown away in onModelRemoved() afterwards.
      //  - Alternatively, we could remove/unregister the OutOfCoreTileManager completely once not used anymore.
      //    However, creating a new OutOfCoreTileManager for the new BVH wouldn't work then, because OutOfCoreTileManager needs to
      //    know the viewer too - which is obtained just once at onModelAdded().
      this.freeAllNodes(false);
    }
  }

  /**
   * Reactivate the iterator at the given iterator ID (when a consolidation iterator is being recreated)
   * @param {number} iteratorId
   * @param {ModelIteratorBVH} iterator
   */
  activateIterator(iteratorId, iterator) {
    this.iteratorIdRegistry[iteratorId] = iterator;
  }

  /**
   * Frees the all resources for all nodes in the OutOfCoreTileManager
   * @param {boolean} [keepNodesInLowestStage=true] - Whether to keep the nodes in the lowest stage or remove from all stages
   */
  freeAllNodes(keepNodesInLowestStage = true) {
    this.bvhNodes.forEach((node) => {// forEach skips empty slots
      // Free resources used by the node
      let freedResources = node.freeAllResources();
      for (let [resourceType, freedAmount] of Object.entries(freedResources)) {
        OutOfCoreTileManager.#resources.get(resourceType).consumed -= freedAmount;
      }

      // Delete all existing tasks
      node.stageTasks = new Map();

      // Reset the node to the first stage
      while (node.currentStage && node.currentStage.previousStage) {
        node.currentStage = node.currentStage.previousStage;
      }
    });

    // Remove the nodes from global node lists
    for (let stage of this.stages) {
      stage.nodes = stage.nodes.filter((node) => node.outOfCoreTileManager !== this);
      stage.inAsyncPromotion = stage.inAsyncPromotion.filter((node) => node.outOfCoreTileManager !== this);
    }

    // Reinsert the nodes in the first stage
    if (keepNodesInLowestStage) {
      this.bvhNodes.forEach((node) => {
        node.currentStage.nodes.push(node);
      });
    } else {
      this.bvhNodes.length = 0;
    }


    if (OutOfCoreTileManager.currentPromotion?.node?.outOfCoreTileManager === this) {
      OutOfCoreTileManager.currentPromotion = undefined;
    }
  }

  /**
   * Updates the cost and transparency flag of a BVH node.
   *
   * @param {number} iteratorId - The ID of the iterator.
   * @param {number} bvhNodeId - The ID of the BVH node.
   * @param {number} cost - The cost to update the BVH node with.
   */
  updateBvhNodeScreenSpaceError(iteratorId, bvhNodeId, cost) {
    let node = this.getBvhNode(bvhNodeId);

    if (!node) {
      return;
    }

    if (node.updateScreenSpaceError(iteratorId, cost, this.getFrameCount(iteratorId))) {
      node.currentStage.nodes.replace(node, node);
    }

    // Track this node for staleness 10 frames from now.
    // When a node stops being rendered, it will become stale and its heap
    // position needs to be updated to reflect the lower priority.
    OutOfCoreTileManager.__trackNodeForStalenessCheck(node);
  }

  /**
   * Removes the locked flag from all tiles. This should only be called,
   * once all candidate renderbatches have been cleared and therefore
   * no old consolidated mesh is any longer referenced.
   *
   * @param {number} iteratorId -- The id of the iterator
   */
  resetLockedTiles(iteratorId) {
    let lockedTiles = this.iteratorLockedTiles.get(iteratorId);
    if (lockedTiles) {
      for (let tileId of lockedTiles) {
        this.unlockTile(iteratorId, tileId);
      }
    }
  }

  /**
   * Unlocks a tile corresponding to the given nodeId
   * @param {number} iteratorId -- The id of the iterator
   * @param {number} bvhNodeId -- The id of the node in the BVH
   */
  unlockTile(iteratorId, bvhNodeId) {
    let node = this.getBvhNode(bvhNodeId);

    let lockedTiles = this.iteratorLockedTiles.get(iteratorId);
    if (lockedTiles.delete(bvhNodeId)) {
      node.lockedCounter--;
    }
    console.assert(lockedTiles && node.lockedCounter >= 0);

    // Apply a demotion that was deferred while it was locked.
    if (node.lockedCounter === 0 && node.pendingDemoteStage) {
      const targetStage = node.pendingDemoteStage;
      node.pendingDemoteStage = null;
      this.demoteToStage(node, targetStage);
    }
  }

  /**
   * Locks a tile corresponding to the given nodeId. While tiles are locked
   * they cannot be demoted.
   *
   * The reason this is needed is, that the RenderScene keeps render batches across frames
   * while doing progressive rendering and we must not free the resources hold by one of these
   * cached batches. Only after the batch has been rendered or the render scene has been
   * reset can we free the resources.
   *
   * @param {number} iteratorId -- The id of the iterator
   * @param {number} bvhNodeId -- The id of the node in the BVH
   */
  lockTile(iteratorId, bvhNodeId) {
    let node = this.getBvhNode(bvhNodeId);
    node.lockedCounter++;

    let lockedTiles = this.iteratorLockedTiles.get(iteratorId);
    if (!lockedTiles) {
      lockedTiles = new Set();
      this.iteratorLockedTiles.set(iteratorId, lockedTiles);
    }
    lockedTiles.add(bvhNodeId);
  }

  /**
   * Increment the frame count
   */
  incrementFrameCount(iteratorId) {
    this.#frameCounts[iteratorId] = (this.#frameCounts[iteratorId] ?? 0) + 1;
  }

  /**
   * Resets the screen space errors in all nodes for the given iterator
   * @param {number} iteratorId - The ID of the iterator
   */
  resetScreenSpaceErrors(iteratorId) {
    // Reset the screen space errors for all nodes
    this.bvhNodes.forEach((node) => {// forEach skips empty slots
      if (node.updateScreenSpaceError(iteratorId, -Infinity, 0)) {
        node.currentStage.nodes.replace(node, node);
      }
      node.updateLastRendered(iteratorId, 0);
    });
  }

  /**
   * Obtains the ID for the given model iterator
   * @param {ModelIteratorBVH} iterator
   * @param {boolean} doNotCreate - Whether to create a new ID if it does not exist
   * @returns
   */
  getIteratorId(iterator, doNotCreate = false) {
    let ids = this.iteratorIdRegistry;

    // Do we already have an entry for the model?
    let index = ids.indexOf(iterator);
    if (index !== -1) {
      return index;
    }

    if (doNotCreate) {
      return undefined;
    }

    // If not, insert the model in the next free slot
    for (let i = 0; true; i++) {
      if (ids[i] === undefined) {
        ids[i] = iterator;
        return i;
      }
    }
  }

  /**
   * Assign a viewer to the OutOfCoreTileManager
   * @param {Viewer3DImpl} viewer - The viewer instance to use for this OutOfCoreTileManager
   */
  #addViewer(viewer) {
    this.#viewers.push(viewer);
  }

  /**
   * Removes a viewer from the OutOfCoreTileManager
   * @param {Viewer3DImpl} viewer - The viewer instance to use for this OutOfCoreTileManager
   * @param {RenderModel} model - The model which is being removed
   */
  #removeViewer(viewer, model) {
    const bvhIterator = model.getIterator();
    const iteratorId = this.iteratorIdRegistry.indexOf(bvhIterator);
    if (iteratorId !== -1) {
      this.releaseIterator(iteratorId);
    }

    // The viewer must be removed last, as releasing the iterator might cause nodes to be freed.
    // In case a UniformUploadTask needs to be freed it still needs the viewer to do that.
    let viewerIndex = this.#viewers.indexOf(viewer);
    if (viewerIndex !== -1) {
      this.#viewers.splice(viewerIndex, 1);
    }
  }

  /**
   * Returns a viewer instance for this OutOfCoreTileManager. There might be multiple viewers
   * associated to this OutOfCoreTileManager if those instances share resources. In such a case,
   * we return the first viewer instance.
   *
   * @returns {Viewer3DImpl} - The viewer instance
   */
  getViewer() {
    return this.#viewers[0];
  }

  /**
   * Get the Renderer associated with this OutOfCoreTileManager
   * @returns {WebGLRenderer} - The renderer instance
   */
  getRenderer() {
    return this.#viewers[0].glrenderer();
  }

  /**
   * Firefox on macOs doesn't support transform feedback currently.
   * Transform feedback doesn't exist in WebGPU. It could be implemented using a compute shader, but currently is not.
   * On Mobile transform feedback can lead to malformed triangles
   * @returns Whether transform feedback can be used on the current platform
   */
  isTransformFeedbackSupported() {
    return !(Autodesk.Viewing.Private.isFirefox() && Autodesk.Viewing.Private.isMac() || USE_WEBGPU || isMobileDevice());
  }

  /**
   * Adds a one shot function to execute during the task processing
   *
   * @param {OneShotTaskFunction} task - The function to execute for the global task
   * @param {number|BvhNode} [priorityOrNode=Infinity] - The priority of the task. If a BvhNode is given, the screenspace error of that node is used as priority.
   * @param {boolean} [resumeExecutingTasks=true] - Whether to call executeTasks after the task has been queued (if there's still time budget available).
   *                                                This should be used if calling addOneShotTask outside of the task processing loop, so that the task
   *                                                doesn't have to wait until the next tick.
   */
  static addOneShotTask(task, priorityOrNode = Infinity, resumeExecutingTasks = true) {
    const newTask = {
      get priority() {
        return priorityOrNode instanceof BvhNode ? priorityOrNode.getCurrentScreenSpaceError(priorityOrNode.getNextStage()) : priorityOrNode;
      },
      task,
      priorityOrNode
    };

    // Insert the task keeping the array sorted by priority
    const index = sortedInsertPosition(OutOfCoreTileManager.oneShotTasks, newTask, (a, b) => b.priority - a.priority);
    OutOfCoreTileManager.oneShotTasks.splice(index, 0, newTask);

    if (!resumeExecutingTasks || OutOfCoreTileManager.resumeExecutingTasksTimeout) {
      return;
    }
    OutOfCoreTileManager.resumeExecutingTasksTimeout = setTimeout(() => {
      OutOfCoreTileManager.resumeExecutingTasksTimeout = undefined;
      if (performance.now() < OutOfCoreTileManager.lastDeadline) {
        OutOfCoreTileManager.executeTasks(this.lastDeadline);
      }
    }, 0);
  }

  /**
   * Adds a task that is run every tick and that is not cleared. Use for fast tasks only.
   * @param {Function} task
   */
  static addRecurringTask(task) {
    OutOfCoreTileManager.recurringTasks.push(task);
  }

  /**
   * Removes a recurring task.
   * @param {Function} task
   */
  static removeRecurringTask(task) {
    const index = OutOfCoreTileManager.recurringTasks.indexOf(task);
    if (index !== -1) {
      OutOfCoreTileManager.recurringTasks.splice(index, 1);
    }
  }

  /**
   * Tracks a node whose screen space error was updated for a future staleness check.
   * The node will be checked for staleness 10 frames from now.
   *
   * @param {BvhNode} node - The node that was updated
   */
  static __trackNodeForStalenessCheck(node) {
    // If the node was already rendered this frame, we don't need to track it again
    if (node.lastRendered === OutOfCoreTileManager.#recentlyRenderedNodeIndex)
    return;

    node.lastRendered = OutOfCoreTileManager.#recentlyRenderedNodeIndex;
    OutOfCoreTileManager.#recentlyRenderedNodes[OutOfCoreTileManager.#recentlyRenderedNodeIndex].push(node);
  }

  /**
   * Checks nodes that were rendered 10 frames ago for staleness and updates their heap positions.
   * This replicates the behavior that previously happened during full list sorting,
   * where nodes that haven't been updated for 10 frames get their screen space error
   * set to 0 (via _getIteratorScreenSpaceError), making them candidates for demotion.
   *
   * Is called as a recurring task at the end of the frame.
   */
  static __checkStaleNodes() {
    // Increment the node index counter to process the next frame's nodes
    OutOfCoreTileManager.#recentlyRenderedNodeIndex = (OutOfCoreTileManager.#recentlyRenderedNodeIndex + 1) % 10;

    const nodesToCheck = OutOfCoreTileManager.#recentlyRenderedNodes[OutOfCoreTileManager.#recentlyRenderedNodeIndex];

    for (let i = 0; i < nodesToCheck.length; i++) {
      const node = nodesToCheck[i];

      // Re-insert the node at its current position to trigger heap rebalancing.
      // This causes getCurrentScreenSpaceError to be re-evaluated during comparisons,
      // which in turn triggers the staleness check in _getIteratorScreenSpaceError.
      // If the node has become stale, its priority will now reflect that (returning 0).
      node.currentStage.nodes.replace(node, node);
    }

    // Clear the array for reuse in the next cycle (10 frames from now)
    nodesToCheck.length = 0;
  }

  /**
   * Performs a promotion of a node to the next stage
   *
   * @param {ActivePromotion} promotion - The promotion to perform.
   * @param {number} deadline - The deadline for the current task processing
   */
  static processPromotion(promotion, deadline) {
    const { node, taskCompletionPromises } = promotion;

    while (performance.now() < deadline) {
      // Check whether the next task can be executed this tick
      let nextTask = node.getNextTask();
      if (nextTask && !nextTask.canBeExecutedThisTick()) {
        return;
      }

      let [resourceCost, moreTasks, taskCompletionPromise] = node.processNextTask(deadline);
      OutOfCoreTileManager.#resources.get(node.getResourceType(NodeStateUpdateOperations.PROMOTE)).consumed += resourceCost;
      const nodeCompleted = !moreTasks;

      if (taskCompletionPromise) {
        taskCompletionPromises.push(taskCompletionPromise);
      }

      if (nodeCompleted) {
        // Update the node stage to the next stage (and mark the promotion as completed)
        OutOfCoreTileManager.#updateNodeStage(
          node,
          NodeStateUpdateOperations.PROMOTE,
          taskCompletionPromises
        );
        return;
      }
    }
  }

  /**
   * @param {Set<ResourceType>} alreadyProcessedResources - Set of resource types that aren't available this tick
   * @param {Stage} stage - The stage from which to select a promotion candidate.
   * @returns {BvhNode|undefined} The highest priority node in that stage, or undefined it cannot be processed.
   */
  static getPromotionCandidateForStage(alreadyProcessedResources, stage) {

    let candidate = stage.nodes.findMin();
    if (!candidate) {
      return;
    }

    // No more promotions for this node possible
    const nextStage = candidate.getNextStage();
    if (!nextStage) {
      return;
    }

    // We no longer want any candidates for this resource. We already tried freeing it and failed
    if (alreadyProcessedResources.has(candidate.getResourceType(NodeStateUpdateOperations.PROMOTE))) {
      return;
    }

    // Are there already too many nodes currently waiting for the promotion to the next stage?
    if (candidate.currentStage.inAsyncPromotion.length >= nextStage.config.inAsyncPromotionLimit) {
      return;
    }

    if (nextStage.config.prerequisites && !nextStage.config.prerequisites(candidate)) {
      return;
    }

    // Make sure, all tasks on the candidate node are initialized
    candidate.initializeTasksForPromotion();

    return candidate;
  }

  static executeOneShotTask(task, deadline) {
    return task.task(deadline);
  }

  /**
  * Run tasks until the deadline.
  * @param {Number} deadline - when to stop processing tasks.
  * @returns {boolean} - Whether more work is to be done.
  */
  static executeTasks(deadline) {
    let moreWorkToDo = true;
    let someWorkDone = false;

    for (const task of OutOfCoreTileManager.recurringTasks) {
      task(deadline);
    }

    // Sort oneshot tasks by priority
    OutOfCoreTileManager.oneShotTasks.sort((a, b) => {
      return b.priority - a.priority;
    });

    OutOfCoreTileManager.#stats.promotedThisFrame = 0;
    OutOfCoreTileManager.#stats.demotedThisFrame = 0;

    // The amount of resources left can become negative, if the resource limit is reduced and
    // not all resources could be freed instantly, because of locked nodes. In that case, we
    // try to free the resources here
    for (let resourceType of OutOfCoreTileManager.#resources.keys()) {
      OutOfCoreTileManager.freeResourcesBelowLimit(resourceType, deadline);
    }

    /** @type {BvhNode|null} */
    let bestCandidate = null; // The best candidate to promote, is not recomputed if one shot tasks are processed before
    let bestCandidatePriority = 0;
    let alreadyProcessedResources = new Set();
    while (true) {
      // Continue an ongoing promotion
      if (OutOfCoreTileManager.currentPromotion) {
        OutOfCoreTileManager.processPromotion(OutOfCoreTileManager.currentPromotion, deadline);
        someWorkDone = true;
      }

      // If we still have time left, start the next promotion or one shot task
      // We don't exit unless we have done at least some work, as otherwise we might
      // spend time sorting tasks and then exit
      if (someWorkDone && performance.now() > deadline) {
        // out of time
        break;
      }

      // Find the next node to promote
      if (!bestCandidate && !OutOfCoreTileManager.currentPromotion) {
        for (let stage of OutOfCoreTileManager._allStages) {
          const candidate = OutOfCoreTileManager.getPromotionCandidateForStage(alreadyProcessedResources, stage);
          if (candidate) {
            if (!bestCandidate || candidate.compare(bestCandidate, candidate.getNextStage(), bestCandidate.getNextStage()) < 0) {
              bestCandidate = candidate;
            }
          }
        }
        bestCandidatePriority = bestCandidate?.getCurrentScreenSpaceError(bestCandidate.getNextStage()) ?? 0;
      }

      // If we have a one shot task that is more important than the best candidate execute it first
      if (OutOfCoreTileManager.oneShotTasks[0]?.priority >= bestCandidatePriority) {
        const nextOneShotTask = OutOfCoreTileManager.oneShotTasks.shift();
        if (!OutOfCoreTileManager.executeOneShotTask(nextOneShotTask, deadline)) {
          // The task was not completed, so we need to re-insert it. We had to remove it earlier
          // as it might have created a new one shot task with a higher priority
          OutOfCoreTileManager.addOneShotTask(nextOneShotTask.task, nextOneShotTask.priorityOrNode, false);
        }
        someWorkDone = true;
      } else {
        // The best candidate is more important than the one shot task or
        // we don't have a candidate or one shot task. We stop processing in the latter case
        if (bestCandidate === null) {

          if (OutOfCoreTileManager._resolveWhenTasksDone) {
            const hasAsyncPromotions = OutOfCoreTileManager._allStages.some(
              (stage) => stage.inAsyncPromotion.length > 0
            );
            if (!hasAsyncPromotions) {
              OutOfCoreTileManager._resolveWhenTasksDone.resolve();
              OutOfCoreTileManager._resolveWhenTasksDone = undefined;
            }
          }
          moreWorkToDo = false;

          break;
        }

        // If a demoteToStage/CANCEL happened after this node was selected as bestCandidate
        // (e.g. via a one-shot task), its stageTasks will have been cleared. Discard the
        // stale candidate so it re-enters the selection loop with fresh task initialization.
        if (!bestCandidate.inPromotion()) {
          bestCandidate = null;
          continue;
        }

        // Attempt to free resource if necessary
        let resourceAvailable = OutOfCoreTileManager.freeResourcesBelowLimit(bestCandidate, deadline);
        someWorkDone = true;
        if (!resourceAvailable) {
          // Try again with nodes that require other resource types
          alreadyProcessedResources.add(bestCandidate.getResourceType(NodeStateUpdateOperations.PROMOTE));
          bestCandidate = null;
          continue;
        }

        OutOfCoreTileManager.currentPromotion = {
          node: bestCandidate,
          taskCompletionPromises: []
        };

        bestCandidate = null;
        bestCandidatePriority = 0;
      }
    }

    this.lastDeadline = deadline;
    return moreWorkToDo;
  }

  /**
   * Tries to free enough resources to satisfy the resource limits. If a comparison node is provided, it will try to free
   * enough memory to enable promoting that node to the next stage. If not enough of the associated resource is available
   * we will free other nodes with a lower weighted screen space error that consume the same resource than the comparison node.
   *
   * @param {BvhNode|ResourceType} comparisonNodeOrResourceType - This can either be a resource type or a node
   *                                        * If it is  a a node, we will try to free sufficient resources
   *                                          to enable promoting this node to the next stage. We will only
   *                                          free other resources, if the screen space error of those nodes
   *                                          is smaller than the error for the comparison node.
   *                                        * If it is a resource type, we will free resources until the
   *                                          limit for that resource is satisfied, not checking for any
   *                                          screen space error.
   * @param {number} deadline             - The deadline for the current task processing
   *
   * @returns {boolean} - Whether the required resources are available
   */
  static freeResourcesBelowLimit(comparisonNodeOrResourceType, deadline) {
    let comparisonNode = null;
    let resourceTypeToFree;
    let resourceTarget;

    if (comparisonNodeOrResourceType instanceof BvhNode) {
      comparisonNode = comparisonNodeOrResourceType;
      resourceTypeToFree = comparisonNode.getResourceType(NodeStateUpdateOperations.PROMOTE);
      resourceTarget = OutOfCoreTileManager.#resources.get(resourceTypeToFree).limit - comparisonNode.getRemainingResourceCost();
    } else {
      resourceTypeToFree = comparisonNodeOrResourceType;
      resourceTarget = OutOfCoreTileManager.#resources.get(resourceTypeToFree).limit;
    }

    if (OutOfCoreTileManager.#resources.get(resourceTypeToFree).consumed <= resourceTarget) {
      return true;
    }

    // Report the first time we reach the GPU memory limit
    if (resourceTypeToFree === ResourceType.GPU_MEMORY) {
      OutOfCoreTileManager.#trackGPUOutOfCore(comparisonNode?.model);
    }

    // Try to find enough nodes that can be demoted to free the resources
    // needed for the comparison node
    let stagesForDemotion = OutOfCoreTileManager._stagesByResourceForDemotion.get(resourceTypeToFree);
    while (OutOfCoreTileManager.#resources.get(resourceTypeToFree).consumed > resourceTarget && performance.now() < deadline) {

      // Insert candidates from all stages
      /** @type {BvhNode} */let bestCandidate = undefined;
      /** @type {Stage} */let bestCandidateStageWithSameResource;
      for (let stage of stagesForDemotion) {

        let currCandidate = stage.nodes.findMax();

        if (!currCandidate) continue;

        // If candidate and comparisonNode are the same, we don't need to compare them
        // because freeing and again uploading the same node wouldn't make any sense
        if (currCandidate === comparisonNode) continue;

        // Ignore locked nodes
        if (currCandidate.lockedCounter !== 0) continue;

        // We might need to demote the candidate through multiple stages to free the desired resource.
        // Find the target stage for the candidate node.
        let currCandidateStageWithSameResource = currCandidate.currentStage;
        while (currCandidateStageWithSameResource.config.resource !== resourceTypeToFree) {
          currCandidateStageWithSameResource = currCandidateStageWithSameResource.previousStage;
        }

        // Check whether this is best candidate available
        if (bestCandidate === undefined ||
        bestCandidate.compare(currCandidate, bestCandidateStageWithSameResource, currCandidateStageWithSameResource) < 0) {
          bestCandidate = currCandidate;
          bestCandidateStageWithSameResource = currCandidateStageWithSameResource;
        }
      }

      if (bestCandidate === undefined) {
        // No more candidates available
        break;
      }

      if (comparisonNode && bestCandidate.compare(comparisonNode, bestCandidateStageWithSameResource, comparisonNode.getNextStage()) <= 0) {
        // Best candidate is more important than the comparison node, stop demoting.
        break;
      }

      // Free the resources for the candidate node
      OutOfCoreTileManager.#updateNodeStage(bestCandidate, NodeStateUpdateOperations.DEMOTE);
    }

    return OutOfCoreTileManager.#resources.get(resourceTypeToFree).consumed <= resourceTarget;
  }

  /**
   * Demote the node to the target stage or don't do anything if the node is already in the target or earlier stage
   * @param {BvhNode} node
   * @param {Stage} targetStage
   */
  demoteToStage(node, targetStage) {
    if (node.currentStage.index < targetStage.index) {
      return;
    }

    // While the node is locked, its consolidated geometry / GPU buffers are still referenced
    // by a render batch that RenderScene caches across progressive frames (see lockTile()).
    // Defer the demotion and apply it from unlockTile() once the last lock is released.
    // If several demotions are requested while locked, keep the lowest (earliest) target stage.
    //
    // Note there can be no concurrent promotion as it is disabled for locked nodes.
    //
    // Note also that deferring can mean the re-promoted / reoptimized result is not visible
    // until the next progressive frame, since the demote (and subsequent re-promotion) only
    // happens after the currently in-flight batch has finished rendering.
    if (node.lockedCounter !== 0) {
      if (!node.pendingDemoteStage || targetStage.index < node.pendingDemoteStage.index) {
        node.pendingDemoteStage = targetStage;
      }
      return;
    }

    OutOfCoreTileManager.#demoteNow(node, targetStage);
  }

  /**
   * Unconditionally demote the node to the target stage. Callers must ensure the node is not
   * locked (see demoteToStage, which defers when locked).
   * @param {BvhNode} node
   * @param {Stage} targetStage
   */
  static #demoteNow(node, targetStage) {
    if (node.inPromotion()) {
      OutOfCoreTileManager.#updateNodeStage(node, NodeStateUpdateOperations.CANCEL);
    }
    while (node.currentStage.index > targetStage.index) {
      OutOfCoreTileManager.#updateNodeStage(node, NodeStateUpdateOperations.DEMOTE);
    }
  }

  /**
   * Sets the limit for a resource type.
   *
   * If necessary, it will free the requested amount of the given resource type (however, this might fail, if nodes are locked)
   *
   * @param {ResourceType} resourceType - The resource type to set the limit for
   * @param {number} value - The new resource limit value.
   */
  static setResourceLimit(resourceType, value) {
    let currentResources = this.#resources.get(resourceType) ?? { consumed: 0, limit: 0 };

    if (resourceType === ResourceType.GPU_MEMORY) {
      value = 0.9 * value; // reduce gpu memory limit to account for Buffer Manager overhead
    }

    currentResources.limit = value;
    this.#resources.set(resourceType, currentResources);

    OutOfCoreTileManager.freeResourcesBelowLimit(resourceType, Infinity);
  }

  /**
   * Returns the frame count for the specified iterator ID.
   * @param {string} iteratorId - The ID of the iterator.
   * @returns {number} The frame count for the specified iterator ID.
   */
  getFrameCount(iteratorId) {
    return this.#frameCounts[iteratorId] ?? 0;
  }

  /**
   * Get the statistics of the OutOfCoreTileManager.
   * @returns {OutOfCoreTileManagerStats} statistics of the OutOfCoreTileManager
   */
  static getStats() {
    if (!OutOfCoreTileManager.#statsUpdateTimer) {
      OutOfCoreTileManager.#statsUpdateTimer = setInterval(OutOfCoreTileManager.updateStats, 1000);
    }

    // these might be null in tests
    const defaultStages = OutOfCoreTileManager._stagesByConfig.get(getDefaultStageConfiguration());
    const rcsStages = OutOfCoreTileManager._stagesByConfig.get(getRcsStageConfiguration());

    const countNodesInStage = (stageName) => {
      const inStages = (stages) => stages?.find((stage) => stage.config.stage === stageName)?.nodes?.length ?? 0;
      return inStages(defaultStages) + inStages(rcsStages);
    };

    return {
      tilesInQueue: countNodesInStage(StageNames.CONSOLIDATION_COMPUTED),
      tilesOnGpu: countNodesInStage(StageNames.OPTIMIZED_ON_GPU),
      promotedLastSecond: OutOfCoreTileManager.#stats.promotedLastSecond,
      demotedLastSecond: OutOfCoreTileManager.#stats.demotedLastSecond,
      promotedThisFrame: OutOfCoreTileManager.#stats.promotedThisFrame,
      demotedThisFrame: OutOfCoreTileManager.#stats.demotedThisFrame,
      resources: OutOfCoreTileManager.#resources
    };
  }

  static updateStats() {
    OutOfCoreTileManager.#stats.promotedLastSecond = OutOfCoreTileManager.#stats.promotedThisSecond;
    OutOfCoreTileManager.#stats.demotedLastSecond = OutOfCoreTileManager.#stats.demotedThisSecond;
    OutOfCoreTileManager.#stats.promotedThisSecond = 0;
    OutOfCoreTileManager.#stats.demotedThisSecond = 0;
  }


  /**
   * Adds a new config to the OutOfCoreTileManager
   * @param {OutOfCoreTileManagerStageConfiguration[]} config
   */
  static #registerStageConfig(config) {
    let stages = OutOfCoreTileManager._stagesByConfig.get(config);
    if (!stages) {
      stages = config.map((stageConfig) => new Stage(stageConfig));
      OutOfCoreTileManager._stagesByConfig.set(config, stages);

      // Register the stale node check as a recurring task if not already registered
      if (!OutOfCoreTileManager.recurringTasks.includes(OutOfCoreTileManager.__checkStaleNodes)) {
        OutOfCoreTileManager.addRecurringTask(OutOfCoreTileManager.__checkStaleNodes);
      }

      for (let i = 0; i < config.length; i++) {
        const stage = stages[i];

        stage.index = i;
        stage.previousStage = i !== 0 ? stages[i - 1] : undefined;
        stage.nextStage = i !== config.length - 1 ? stages[i + 1] : undefined;

        const demotionStageList = stages.slice(i);
        OutOfCoreTileManager._allStages.push(stage);

        const resource = stage.config.resource;
        if (!OutOfCoreTileManager.#resources.get(resource)) {
          OutOfCoreTileManager.#resources.set(resource, { consumed: 0, limit: 0 });
        }

        // If there is a previous stage, we can demote nodes in this stage and therefore
        // register this node for its resource type as demotable
        let globalDemotionStageList = OutOfCoreTileManager._stagesByResourceForDemotion.get(resource);
        if (!globalDemotionStageList) {
          globalDemotionStageList = [];
          OutOfCoreTileManager._stagesByResourceForDemotion.set(resource, globalDemotionStageList);
        }
        for (let stage of demotionStageList) {
          if (!globalDemotionStageList.includes(stage)) {
            globalDemotionStageList.push(stage);
          }
        }
      }
    }
    return stages;
  }

  /**
   * Register a new viewer instance with the OutOfCoreTileManager
   * @param {Viewer3DImpl} viewer
   */
  static registerViewer(viewer) {
    viewer._ooctmCallbackOnModelAdded = OutOfCoreTileManager.#onModelAdded.bind(undefined, viewer);
    viewer._ooctmCallbackOnModelRemoved = OutOfCoreTileManager.#onModelRemoved.bind(undefined, viewer);
    viewer._ooctmCallbackOnVisibilityChanged = OutOfCoreTileManager.#onVisibilityChanged.bind(undefined, viewer);

    viewer.api.addEventListener(EventTypes.MODEL_ADDED_EVENT, viewer._ooctmCallbackOnModelAdded);
    viewer.api.addEventListener(EventTypes.MODEL_REMOVED_EVENT, viewer._ooctmCallbackOnModelRemoved);
    viewer.api.addEventListener(EventTypes.VIEWER_VISIBILITY_CHANGED, viewer._ooctmCallbackOnVisibilityChanged);

    if (USE_WEBGPU) {
      viewer.api.addEventListener(EventTypes.PREF_CHANGED_EVENT, (event) => {
        if (event.name === Prefs3D.GPU_MEMORY_LIMIT) {
          setGPU_MEMORY_LIMIT(event.value);
          OutOfCoreTileManager.setResourceLimit(ResourceType.GPU_MEMORY, event.value);
        }
      });
    }
  }

  /**
   * Unregisters a viewer instance
   * @param {Viewer3DImpl} viewer
   */
  static unregisterViewer(viewer) {
    viewer.api.removeEventListener(EventTypes.MODEL_ADDED_EVENT, viewer._ooctmCallbackOnModelAdded);
    viewer.api.removeEventListener(EventTypes.MODEL_REMOVED_EVENT, viewer._ooctmCallbackOnModelRemoved);
    viewer.api.removeEventListener(EventTypes.VIEWER_VISIBILITY_CHANGED, viewer._ooctmCallbackOnVisibilityChanged);

    viewer._ooctmCallbackOnModelAdded = undefined;
    viewer._ooctmCallbackOnModelRemoved = undefined;
    viewer._ooctmCallbackOnVisibilityChanged = undefined;
  }

  /**
   * The visibility for a viewer has changed
   * @param {Viewer3DImpl} viewer
   * @param {{visible: boolean}} event        - The event object
   */
  static #onVisibilityChanged(viewer, event) {
    if (!event.visible) {
      // If the viewer has been hidden, we have to reset the error for
      // all nodes, because the screen space error is not valid anymore
      for (let model of viewer.api.getAllModels()) {
        const manager = OutOfCoreTileManager.getManagerForModel(model);
        const iteratorID = manager?.getIteratorId(model.getIterator(), true);
        if (manager && iteratorID) {
          manager.resetScreenSpaceErrors(iteratorID);
        }
      }
    }
  }

  /**
   * A model has been added to a viewer
   * @param {Viewer3DImpl} viewer               - The viewer instance
   * @param {{model: RenderModel}} event        - The event object
   */
  static #onModelAdded(viewer, event) {
    let outOfCoreTileManager = OutOfCoreTileManager.getManagerForModel(event.model);

    // If we did not find any manager, we create a new one
    if (!outOfCoreTileManager) {
      outOfCoreTileManager = new OutOfCoreTileManager(event.model);

      // If this model is using a LeechViewer, we also store a reference with the leechViewerKey
      if (event.model.leechViewerKey !== undefined) {
        OutOfCoreTileManager.modelManagerRegistry.set(event.model.leechViewerKey, outOfCoreTileManager);
      }
    }
    OutOfCoreTileManager.modelManagerRegistry.set(event.model, outOfCoreTileManager);

    OutOfCoreTileManager.#t1_GPUUpload = undefined;

    // Assign the the viewer to the out-of-core tile manager
    outOfCoreTileManager.#addViewer(viewer);
  }

  /**
   * A model has been removed from a viewer
   * @param {Viewer3DImpl} viewer               - The viewer instance
   * @param {{model: RenderModel}} event        - The event object
   */
  static #onModelRemoved(viewer, event) {
    let outOfCoreTileManager = OutOfCoreTileManager.modelManagerRegistry.get(event.model);
    if (outOfCoreTileManager) {
      outOfCoreTileManager.#removeViewer(viewer, event.model);
    }

    OutOfCoreTileManager.modelManagerRegistry.delete(event.model);

    let usageCount = 0;
    if (event.model.leechViewerKey !== undefined) {
      usageCount = Array.from(OutOfCoreTileManager.modelManagerRegistry.keys()).filter((x) => x.leechViewerKey === event.model.leechViewerKey).length;

      // If we no longer have any reference to the leechViewerKey, we can also remove that entry
      if (usageCount === 0) {
        OutOfCoreTileManager.modelManagerRegistry.delete(event.model.leechViewerKey);
      }
    }

    // Remove all nodes for this OutOfCoreTileManager from global node lists, if there is no
    // longer any iterator using this OutOfCoreTileManager
    if (usageCount === 0) {
      for (let stage of outOfCoreTileManager.stages) {
        stage.nodes = stage.nodes.filter((node) => node.outOfCoreTileManager !== outOfCoreTileManager);
        stage.inAsyncPromotion = stage.inAsyncPromotion.filter((node) => node.outOfCoreTileManager !== outOfCoreTileManager);
      }
    }

    // Tell the iterator, that the OutOfCoreTileManager has been released (this happens
    // when a model is hidden in an aggregated view. In that case, the ressources allocated
    // by the OutOfCoreTileManager are freed, but the modelIterator itself stays assigned
    // to the model and is reused when the model is shown again)
    if (event.model.getIterator().unregisterOutOfCoreTileManager) {
      event.model.getIterator().unregisterOutOfCoreTileManager();
    }

    // TODO: We probably should unregister no longer needed stage configurations. At the moment,
    //       we only have a single stage configuration and leaving the stages registered should not
    //       cause any harm. Only once we have a larger number, it might introduce some overhead traversing
    //       the stages.
  }

  /**
   * Gets the OutOfCoreTileManager for a model
   * @param {RenderModel} model
   * @return {OutOfCoreTileManager}
   */
  static getManagerForModel(model) {
    let outOfCoreTileManager;
    // First check, whether we already have registered a model with the same leechViewerKey
    // (i.e. whether this model shares resources with a different model). In that case, we
    // we will use the already registered OutOfCoreTileManager for the referenced model
    OutOfCoreTileManager._updateLeechViewerKeysInModelRegistry();
    if (model.leechViewerKey !== undefined) {
      outOfCoreTileManager = OutOfCoreTileManager.modelManagerRegistry.get(model.leechViewerKey);
    }

    // Check, whether the model has been registered directly with the out-of-core manager
    if (!outOfCoreTileManager) {
      outOfCoreTileManager = OutOfCoreTileManager.modelManagerRegistry.get(model);
    }
    return outOfCoreTileManager;

  }

  /**
   * Updates the leechViewerKeys in the model registry
   *
   * It can happen that models are added to the registry, before their leechViewerKey is set. In that case, we
   * would not have them under the correct key in the registry. This function updates the registry to ensure that
   * all models are registered under the correct key.
   */
  static _updateLeechViewerKeysInModelRegistry() {
    for (let [existingModel, outOfCoreTileManager] of OutOfCoreTileManager.modelManagerRegistry.entries()) {
      if (existingModel.leechViewerKey && !OutOfCoreTileManager.modelManagerRegistry.has(existingModel.leechViewerKey)) {
        OutOfCoreTileManager.modelManagerRegistry.set(existingModel.leechViewerKey, outOfCoreTileManager);
      }
    }
  }

  /**
   * Updates the resource consumption for a given resource type by the given delta
   *
   * @param {ResourceType} resourceType - The resource type to update
   * @param {number} resourceDelta      - The delta to add to the resource consumption
   */
  static _updateResourceConsumption(resourceType, resourceDelta) {
    OutOfCoreTileManager.#resources.get(resourceType).consumed += resourceDelta;
  }

  /**
   * Moves a node to a new stage (either upwards or downwards)
   *
   * @param {BvhNode} node - The node to add to the stage
   * @param {NodeStateUpdateOperations} operation - Whether the node should be promoted to the next stage,
   *                                                demoted to the previous stage or the current promotion should be cancelled.
   * @param {Promise[]} [taskCompletionPromises] - Promises that should be awaited before the node is added to the new stage
   */
  static async #updateNodeStage(node, operation, taskCompletionPromises) {
    // If the node is the current promotion, we need to clear the current promotion variable
    if (OutOfCoreTileManager.currentPromotion?.node === node) {
      OutOfCoreTileManager.currentPromotion = undefined;
    }

    // for promotions, the resources for the ongoing promotion are already allocated when the promotion is initiated.
    if (operation != NodeStateUpdateOperations.PROMOTE) {
      const resourceType = node.getResourceType(operation);
      const freedResources = node.freeResources(operation);
      OutOfCoreTileManager.#resources.get(resourceType).consumed -= freedResources;
    }

    // Remove the node from the current stage
    node.currentStage.nodes.remove(node);

    // Handle async transitions
    if (taskCompletionPromises?.length > 0) {
      console.assert(operation === NodeStateUpdateOperations.PROMOTE, 'We only support async transitions for promotions, not for demotions');

      node.currentStage.inAsyncPromotion.push(node);
      await Promise.all(taskCompletionPromises);

      if (!node.currentStage.inAsyncPromotion.includes(node)) {
        // If the node is no longer in the inAsyncPromotion array at this point, this means that the async transition
        // has been canceled during the execution of the stage transition, in that case we
        // no longer have to add the node to the new stage and instead stop here
        return;
      }
    }

    // If the node is still in the inAsyncPromotion array, we need to remove it
    let indexInAsyncPromotion = node.currentStage.inAsyncPromotion.indexOf(node);
    if (indexInAsyncPromotion !== -1) {
      node.currentStage.inAsyncPromotion.splice(indexInAsyncPromotion, 1);
    }

    // Update the current stage of the node
    if (operation === NodeStateUpdateOperations.PROMOTE) {
      node.currentStage = node.getNextStage();

      OutOfCoreTileManager.#stats.promotedThisSecond++;
      OutOfCoreTileManager.#stats.promotedThisFrame++;
    } else if (operation === NodeStateUpdateOperations.DEMOTE) {
      node.currentStage = node.getPreviousStage();

      OutOfCoreTileManager.#stats.demotedThisSecond++;
      OutOfCoreTileManager.#stats.demotedThisFrame++;
    }

    // Add the node at the correct position in the new stage
    node.currentStage.nodes.push(node);
  }

  /**
    * Free all GPU Resources in the case of a context loss.
    */
  static resetAfterContextLoss() {
    for (let manager of OutOfCoreTileManager.modelManagerRegistry.values()) {
      manager.freeAllNodes();
    }
  }

  /**
   * Add analytics for GPU out-of-core, including:
   * - Time to reach the GPU memory limit the first time, if it was reached at all
   *
   * Note the OutOfCoreTileManager operates on multiple models, so there is no specific URL attached to the event.
   *
   * @param {RenderModel} [model]
   */
  static #trackGPUOutOfCore(model) {
    // only track the first time
    if (OutOfCoreTileManager.#t1_GPUUpload) {
      return;
    }

    OutOfCoreTileManager.#t1_GPUUpload = performance.now();
    const stats = OutOfCoreTileManager.getStats();
    const properties = {
      memory_limit_time: OutOfCoreTileManager.#t1_GPUUpload - OutOfCoreTileManager.#t0_ModelCreation,
      consolidation_memory_consumed: stats.gpuMemoryConsumed,
      consolidation_memory_limit: stats.gpuMemoryLimit,
      tiles_in_queue: stats.tilesInQueue,
      tiles_on_gpu: stats.tilesOnGpu,
      urn: model?.getData()?.urn ?? 'NOT_SET'
    };
    analytics.track('viewer.model.gpu_out_of_core', properties);
  }

  static setModelLoadStartedTimestamp() {
    this.#t0_ModelCreation = performance.now();
  }

  /**
   * Only for testing! Make sure that the viewer mainloop is running - otherwise it will never resolve.
   * @returns {Promise<void>} A promise that resolves when all pending tasks are done. Note that resolving only waits
   *                          for the next time where there is no more task to process. It doesn't guarantee anything
   *                          about tasks that may come afterwards, e.g., due to loading or camera movements.
   **/
  static async __debugWaitForPendingTasks() {
    // Create Promise with resolve if not existing already
    if (!OutOfCoreTileManager._resolveWhenTasksDone) {
      // Note: The code below can soon be replaced by:
      //       OutOfCoreTileManager._resolveWhenTasksDone = Promise.withResolvers();
      //       as soon as we can use Promise.withResolvers() in LMV.
      let resolve;
      let reject;
      let promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
      });
      OutOfCoreTileManager._resolveWhenTasksDone = {
        promise: promise,
        resolve: resolve,
        reject: reject
      };
    }
    return OutOfCoreTileManager._resolveWhenTasksDone.promise;
  }
}